import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Collapse,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Group,
  Receipt,
  Visibility,
  VisibilityOff,
  Refresh,
  ArrowForward,
  Dashboard as DashboardIcon,
  Assessment,
  Timeline,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getRebalance, GetStrategyVariance } from "../../Redux/Actions/transactionService";
import { useNavigate } from "react-router-dom";
import { GetUserInfo, GetUserRoles} from "../../Redux/Actions/userStatusService";
import { useUser } from "../../Context/UserContext";
import { usePermissions } from "../../Context/PermissionContext";

function DashboardRoutes() {
  const dispatch = useDispatch();
  const { orders } = useSelector((state) => state.transaction);
  const { users } = useSelector((state) => state.userStatus);
  const [varianceData, setVarianceData] = useState([]);
  const { userRole } = useUser();
  const { permissions, hasPermission } = usePermissions();
  const portal = userRole?.toLowerCase();
  const [pendingOrdersOpen, setPendingOrdersOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMetrics, setShowMetrics] = useState(true);
  const navigate = useNavigate();

  // Calculate real metrics from data
  const dashboardMetrics = {
    totalPortfolio: orders.reduce((sum, order) => sum + (parseFloat(order?.amount) || 0), 0),
    dailyPnL: varianceData.reduce((sum, variance) => {
      // Calculate P&L from variance data
      const pnl = variance?.pnl || 0;
      return sum + (parseFloat(pnl) || 0);
    }, 0),
    activeStrategies: orders.length > 0 ? new Set(orders.map(order => order?.strategy?.id)).size : 0,
    pendingOrders: orders.length,
    totalInvestors: varianceData.length > 0 ? varianceData.length : (users?.length || 0),
    monthlyReturn: orders.length > 0 && varianceData.length > 0 ? 
      (varianceData.reduce((sum, variance) => sum + (parseFloat(variance?.pnl || 0), 0)) / 
       orders.reduce((sum, order) => sum + (parseFloat(order?.amount) || 0), 0)) * 100 : 0,
    riskLevel: "Medium",
    lastUpdated: "2 minutes ago"
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        let entityId = null;
        let userInfoResponseForEntity = null;

        if (portal?.toLowerCase() === "manager") {
          userInfoResponseForEntity = await dispatch(
            GetUserInfo({ role: portal })
          ).unwrap();
        } else if (users && users.length > 0) {
          const parent = users[0]?.parentUserRole?.id;

          if (parent) {
            userInfoResponseForEntity = await dispatch(
              GetUserInfo({ role: portal, id: parent })
            ).unwrap();
          }
        }
        entityId = userInfoResponseForEntity?.data?.entityInfo?.id;
    
        // Only proceed with API calls if we have a valid entity ID
        if (entityId) {
          
          // Define additional parameters based on user role
          const additionalParams = userRole === "SUB_MANAGER" && users && users.length > 0
            ? { parent: users[0]?.parentUserRole?.id }
            : {};
          
          // Check permissions for SUB_MANAGER role
          const shouldCallGetRebalance = userRole !== "SUB_MANAGER" || hasPermission("getOrders");
          const shouldCallGetVariance = userRole !== "SUB_MANAGER" || hasPermission("getVariance");
          
          
          // Prepare API calls based on permissions
          const apiCalls = [];
          
          if (shouldCallGetRebalance) {
            apiCalls.push(
              dispatch(getRebalance({ 
                ...additionalParams,
              }))
            );
          }
          
          if (shouldCallGetVariance) {
            apiCalls.push(
              dispatch(GetStrategyVariance())
            );
          }
          
          // Execute API calls
          if (apiCalls.length > 0) {
            const responses = await Promise.all(apiCalls);
            
            let rebalanceResponse = null;
            let varianceResponse = null;
            
            // Map responses to their corresponding API calls
            if (shouldCallGetRebalance && shouldCallGetVariance) {
              // Both APIs called
              rebalanceResponse = responses[0];
              varianceResponse = responses[1];
            } else if (shouldCallGetRebalance) {
              // Only getRebalance called
              rebalanceResponse = responses[0];
            } else if (shouldCallGetVariance) {
              // Only GetStrategyVariance called
              varianceResponse = responses[0];
            }
            
            // Handle getRebalance response
            if (shouldCallGetRebalance && rebalanceResponse) {
              if (rebalanceResponse.payload?.statusCode === 200) {
              } else {
                console.error("Failed to fetch rebalance data:", rebalanceResponse);
              }
            }
            
            // Handle GetStrategyVariance response
            if (shouldCallGetVariance && varianceResponse) {
              if (varianceResponse.payload?.statusCode === 200) {
                setVarianceData(varianceResponse.payload.data || []);
              } else {
                console.error("Failed to fetch variance data:", varianceResponse);
              }
            }
          } else {
         
          }
        } else {
         
          
          // For SUB_MANAGER without entity access, show limited data
          if (userRole === "SUB_MANAGER") {
            // You can set default/limited data here
            setVarianceData([]);
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, portal, users]);

  // Debug variance data

  return (
    <Box
      component="main"
      sx={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        minHeight: '100vh',
        flexGrow: 1,
        overflow: 'auto'
      }}
    >
      <Container 
        maxWidth="xxl" 
        sx={{ 
          pt: 12, 
          pb: 4,
          minHeight: '100vh'
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  color: '#000000', 
                  fontWeight: 700,
                  mb: 1
                }}
              >
                Manager Dashboard
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#666', 
                  fontWeight: 400 
                }}
              >
               Manage your strategies, portfolio and investors
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip 
                label={`Last updated: ${dashboardMetrics.lastUpdated}`}
                sx={{ 
                  bgcolor: '#e3f2fd', 
                  color: '#1976d2',
                  border: '1px solid #bbdefb'
                }}
              />
              <Tooltip title="Refresh Data">
                <IconButton 
                  onClick={() => window.location.reload()}
                  sx={{ 
                    bgcolor: '#e3f2fd', 
                    color: '#1976d2',
                    border: '1px solid #bbdefb',
                    '&:hover': { bgcolor: '#bbdefb' }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

                  {/* Key Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255,255,255,0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-4px)' },
              position: 'relative'
            }}>
              <Chip 
                label="Coming Soon"
                sx={{ 
                  position: 'absolute',
                  top: -2,
                  right: '32%',
                  zIndex: 10,
                  background: 'linear-gradient(135deg,rgb(31, 64, 210) 0%,rgb(38, 13, 148) 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: '24px',
                  '&::before': {
                    content: '"🚀"',
                    marginRight: '4px'
                  }
                }}
              />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Portfolio
                  </Typography>
                  <Avatar sx={{ bgcolor: '#4caf50', width: 48, height: 48 }}>
                    <AccountBalance />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1 }}>
                  ${(dashboardMetrics.totalPortfolio / 1000).toFixed(0)}K
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />
                                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                      +{dashboardMetrics.monthlyReturn.toFixed(2)}% this month
                    </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255,255,255,0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-4px)' },
              position: 'relative'
            }}>
             <Chip 
                label="Coming Soon"
                sx={{ 
                  position: 'absolute',
                  top: -2,
                  right: '32%',
                  zIndex: 10,
                  background: 'linear-gradient(135deg,rgb(31, 64, 210) 0%,rgb(38, 13, 148) 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: '24px',
                  '&::before': {
                    content: '"🚀"',
                    marginRight: '4px'
                  }
                }}
              />
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Daily P&L
                  </Typography>
                  <Avatar sx={{ bgcolor: dashboardMetrics.dailyPnL >= 0 ? '#4caf50' : '#f44336', width: 48, height: 48 }}>
                    {dashboardMetrics.dailyPnL >= 0 ? <TrendingUp /> : <TrendingDown />}
                  </Avatar>
                </Box>
                                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: dashboardMetrics.dailyPnL >= 0 ? '#2e7d32' : '#d32f2f',
                    mb: 1 
                  }}>
                    ${dashboardMetrics.dailyPnL.toFixed(0)}
                  </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {dashboardMetrics.dailyPnL >= 0 ? (
                    <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />
                  ) : (
                    <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />
                  )}
                  <Typography variant="body2" sx={{ 
                    color: dashboardMetrics.dailyPnL >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 500 
                  }}>
                    {dashboardMetrics.dailyPnL >= 0 ? '+' : ''}{dashboardMetrics.monthlyReturn.toFixed(2)}% return
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255,255,255,0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-4px)' }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Active Strategies
                  </Typography>
                  <Avatar sx={{ bgcolor: '#2196f3', width: 48, height: 48 }}>
                    <Assessment />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
                  {dashboardMetrics.activeStrategies}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Timeline sx={{ color: '#2196f3', fontSize: 20 }} />
                                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                      {dashboardMetrics.activeStrategies} unique strategies
                    </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255,255,255,0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': { transform: 'translateY(-4px)' }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Investors
                  </Typography>
                  <Avatar sx={{ bgcolor: '#ff9800', width: 48, height: 48 }}>
                    <Group />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100', mb: 1 }}>
                  {dashboardMetrics.totalInvestors}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Group sx={{ color: '#ff9800', fontSize: 20 }} />
                                      <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                      {dashboardMetrics.totalInvestors} total investors
                    </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Orders Section */}
          <Grid item xs={12} md={6}>
            <Card sx={{  
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              height: '100%',
              minHeight: 400
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#2096f3', width: 40, height: 40 }}>
                      <Receipt />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                        Pending Orders
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {orders.length} orders awaiting execution
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={`${orders.length} Pending`}
                      color="warning"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ flex: 1 }}>
                  {orders.length > 0 ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Recent Orders
                      </Typography>
                      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                        {orders.slice(0, 5).map((order, index) => (
                          <Box 
                            key={index}
                            sx={{ 
                              p: 2, 
                              mb: 1, 
                              bgcolor: '#eff3f7', 
                              borderRadius: 2,
                              border: '1px solid rgba(244,67,54,0.1)'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                                  {order.strategy?.name || 'Unknown Strategy'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Order #{order.id?.slice(-8) || 'N/A'}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
                                  ${parseFloat(order?.amount || 0).toFixed(2)}
                                </Typography>
                                <Chip 
                                  label={order.status || 'PENDING'} 
                                  size="small" 
                                  color="warning"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      py: 4
                    }}>
                      <Avatar sx={{ bgcolor: '#4caf50', width: 64, height: 64, mb: 2 }}>
                        <TrendingUp />
                      </Avatar>
                      <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600, mb: 1 }}>
                        All Clear!
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        No pending orders at the moment
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowForward />}
                    onClick={() => navigate("/managerorders")}
                    sx={{ 
                      bgcolor: '#2096f3',
                      '&:hover': { bgcolor: '#2056f3' },
                      borderRadius: 2,
                      py: 1.5
                    }}
                  >
                    View All Orders
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Strategies Section */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              background: 'rgba(255,255,255,0.95)', 
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              height: '100%',
              minHeight: 400
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#2196f3', width: 40, height: 40 }}>
                      <Assessment />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                        Active Strategies
                      </Typography>
                                          <Typography variant="body2" color="text.secondary">
                      {dashboardMetrics.activeStrategies} unique strategies
                    </Typography>
                    </Box>
                  </Box>
                  <Chip 
                    label={`${dashboardMetrics.activeStrategies} Active`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    py: 4
                  }}>
                    <Avatar sx={{ bgcolor: '#2196f3', width: 80, height: 80, mb: 3 }}>
                      <Assessment sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 700, mb: 1 }}>
                      {dashboardMetrics.activeStrategies}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#333', fontWeight: 600, mb: 2 }}>
                      Active Strategies
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                      Your portfolio is managed by {dashboardMetrics.activeStrategies} unique trading strategies
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                      <Chip 
                        label="Low Risk" 
                        color="success" 
                        variant="outlined"
                        size="small"
                      />
                      <Chip 
                        label="High Yield" 
                        color="primary" 
                        variant="outlined"
                        size="small"
                      />
                      <Chip 
                        label="Diversified" 
                        color="info" 
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    <LinearProgress 
                      variant="determinate" 
                      value={75} 
                      sx={{ 
                        width: '100%', 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: 'rgba(33,150,243,0.1)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#2196f3',
                          borderRadius: 4
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      75% Performance Target Met
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowForward />}
                    onClick={() => navigate("/strategy")}
                    sx={{ 
                      bgcolor: '#2196f3',
                      '&:hover': { bgcolor: '#1976d2' },
                      borderRadius: 2,
                      py: 1.5
                    }}
                  >
                    Manage Strategies
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Assets Section */}
          {/* <Grid item xs={12} md={3}>
            <Paper
              elevation={3}
              sx={{ padding: 2, height: "180px", position: "relative" }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">Assets</Typography>
                <Button
                
                >
                  Show Assets
                </Button>
              </Box>
              <Box mt={2}>
                <Typography>No assets available.</Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ position: "absolute", bottom: "16px", left: "16px" }}
              >
                View All Assets
              </Button>
            </Paper>
          </Grid> */}

          {/* Other Section */}
          {/* <Grid item xs={12} md={3}>
            <Paper
              elevation={3}
              sx={{ padding: 2, height: "180px", position: "relative" }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">Other Section</Typography>
                <Button
                >
                  Show Other Section
                </Button>
              </Box>
              <Box mt={2}>
                <Typography>No data available.</Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ position: "absolute", bottom: "16px", left: "16px" }}
              >
                View All Other Section
              </Button>
            </Paper>
          </Grid> */}
        </Grid>

        {/* Loader for API calls */}
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(5px)",
              zIndex: 9999,
            }}
          >
            <Card sx={{ 
              p: 4, 
              borderRadius: 3,
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={60} sx={{ color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                  Loading Dashboard Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fetching real-time trading information...
                </Typography>
              </Box>
            </Card>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default DashboardRoutes;
